import { useRef, useState } from "react";
import { Button, Modal } from "../../../ui";
import { uploadDashboardRequestFileAPI } from "../../../../utils/api/ludDashboard";

// Choose a file -> see its name -> Upload -> Confirm -> uploaded. Nothing leaves the
// browser before the confirmation; the server validates the file type, role and
// request stage again. While the upload runs everything is locked (no double submit).
const DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
const SHEET_TYPES = ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "text/csv", "application/csv", "application/zip", "application/x-zip-compressed"];
export const ACCEPTED = { contract: DOCUMENT_TYPES, signedContract: DOCUMENT_TYPES, statement: [...DOCUMENT_TYPES, ...SHEET_TYPES], taxInvoice: [...DOCUMENT_TYPES, ...SHEET_TYPES] };
const MAX_BYTES = 10 * 1024 * 1024;

const sizeText = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

export function validateFile(file, kind, t) {
  if (!file) return "";
  if (!file.size) return t.row.fileEmpty;
  if (file.size > MAX_BYTES) return t.row.fileSize;
  if (!(ACCEPTED[kind] || DOCUMENT_TYPES).includes(String(file.type || "").toLowerCase())) return t.row.fileType;
  return "";
}

export default function UploadFileDialog({ requestId, kind, title, confirmLabel, t, onClose, onDone }) {
  const token = localStorage.getItem("auth-token");
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("choose");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const input = useRef(null);
  const problem = validateFile(file, kind, t);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await uploadDashboardRequestFileAPI(token, requestId, kind, file);
      await onDone?.();
    } catch (failure) {
      setError(failure?.message || t.row.uploadFailed);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      size="md"
      busy={busy}
      closeLabel={t.row.close}
      onClose={onClose}
      footer={
        step === "choose" ? (
          <>
            <Button variant="primary" appearance="filled" disabled={!file || Boolean(problem)} onClick={() => setStep("confirm")}>{t.row.upload}</Button>
            <Button variant="neutral" appearance="soft" onClick={onClose}>{t.row.close}</Button>
          </>
        ) : (
          <>
            <Button variant="primary" appearance="filled" loading={busy} disabled={busy} onClick={confirm}>{busy ? t.row.uploading : confirmLabel}</Button>
            <Button variant="neutral" appearance="soft" disabled={busy} onClick={() => setStep("choose")}>{t.row.back}</Button>
          </>
        )
      }
    >
      <div className="lud-ur-dialog-body">
        {step === "choose" ? (
          <>
            <input ref={input} type="file" aria-label={t.row.chooseFile} accept={(ACCEPTED[kind] || DOCUMENT_TYPES).join(",")} onChange={(event) => setFile(event.target.files?.[0] || null)} />
            <p className="lud-ur-muted" dir="auto">{file ? `${file.name} · ${sizeText(file.size)}` : t.row.noFile}</p>
            {problem && <p className="lud-ur-error" role="alert">{problem}</p>}
          </>
        ) : (
          <p dir="auto">{t.row.confirmUploadText(file?.name || "")}</p>
        )}
        {error && <p className="lud-ur-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}
